import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameMonth, isSameDay, format, addMonths, subMonths, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { bookingService, talentGroupService } from '../api/apiService';
import moment from 'moment';

const CalendarContext = createContext();

export function useCalendar() {
  return useContext(CalendarContext);
}

export function CalendarProvider({ children, initialEvents = [] }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState('week'); // Changed from 'month' to 'week'
  const [events, setEvents] = useState(initialEvents);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Track if we have initial events provided
  const hasInitialEvents = useRef(initialEvents.length > 0);
  
  // State for filter options
  const [userOptions, setUserOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [setOptions, setSetOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  
  // Talent groups state
  const [talentGroups, setTalentGroups] = useState([]);
  
  // Filters for booking events
  const [filters, setFilters] = useState({
    userEmail: null,
    bookingStatus: null,
    client: null,
    set: null,
    location: null,
    searchTerm: null,
    talentGroup: null
  });

  // Use a useRef to track if this is the first render
  const isInitialMount = useRef(true);
  
  // Track the last fetch parameters to avoid duplicate API calls
  const lastFetchParams = useRef({
    fromDate: null,
    toDate: null,
    statusFilter: null,
    filters: {}
  });
  
  // Generate filter options from event data
  const populateFilterOptions = (eventData) => {
    // Extract unique values for each filter
    
    // For proposedBy (users)
    const uniqueUsers = [...new Set(eventData
      .filter(event => event.proposedBy)
      .map(event => event.proposedBy))];
    
    const userOpts = [
      { value: null, label: 'All Users' },
      ...uniqueUsers.map(user => ({ value: user, label: user }))
    ];
    
    // For clients
    const uniqueClients = [...new Set(eventData
      .filter(event => event.client)
      .map(event => event.client))];
    
    const clientOpts = [
      { value: null, label: 'All Clients' },
      ...uniqueClients.map(client => ({ value: client, label: client }))
    ];
    
    // For sets - always include "On Model" and "on Produkt"
    const uniqueSets = [...new Set(eventData
      .filter(event => event.set)
      .map(event => event.set))];
      
    // Ensure our two main set options are always included
    if (!uniqueSets.includes('On Model')) uniqueSets.push('On Model');
    if (!uniqueSets.includes('on Produkt')) uniqueSets.push('on Produkt');
    
    const setOpts = [
      { value: null, label: 'All Sets' },
      ...uniqueSets.map(set => ({ value: set, label: set }))
    ];
    
    // For locations
    const uniqueLocations = [...new Set(eventData
      .filter(event => event.location)
      .map(event => {
        // Extract location string from object or return the string directly
        if (event.location && typeof event.location === 'object') {
          return event.location.short || event.location.name || event.location.city || '';
        } else if (typeof event.location === 'string') {
          return event.location;
        }
        return '';
      })
      .filter(location => location !== ''))]; // Remove empty strings
    
    const locationOpts = [
      { value: null, label: 'All Locations' },
      ...uniqueLocations.map(location => ({ value: location, label: location }))
    ];
    
    // For statuses
    const uniqueStatuses = [...new Set(eventData
      .filter(event => event.status)
      .map(event => event.status))];
    
    const statusOpts = [
      { value: null, label: 'All Status' },
      ...uniqueStatuses.map(status => ({ value: status, label: status }))
    ];
    
    // Update state with new options
    setUserOptions(userOpts);
    setClientOptions(clientOpts);
    setSetOptions(setOpts);
    setLocationOptions(locationOpts);
    setStatusOptions(statusOpts);
  };

  // Fetch events when current date or view changes
  useEffect(() => {
    // Skip the first render to avoid double fetching
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchBookingEvents();
      return;
    }
    
    // Use a timer to debounce the API calls
    const timer = setTimeout(() => {
      fetchBookingEvents();
    }, 300); // 300ms debounce
    
    return () => clearTimeout(timer);
  }, [currentDate, view, JSON.stringify(filters)]); // Use JSON.stringify to compare objects

  // Fetch booking events from API based on date range and filters
  const fetchBookingEvents = async (forceRefresh = false) => {
    // If we have initial events provided, don't fetch from API
    if (hasInitialEvents.current && !forceRefresh) {
      console.log('CalendarProvider: Using initial events, skipping API fetch');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      let startDate, endDate;
      
      if (view === 'month') {
        startDate = startOfMonth(currentDate);
        endDate = endOfMonth(currentDate);
      } else if (view === 'week') {
        startDate = startOfWeek(currentDate);
        endDate = endOfWeek(currentDate);
      } else { // day view
        startDate = currentDate;
        endDate = currentDate;
      }
      
      // Extract status filter if present
      const statusFilter = filters.bookingStatus ? filters.bookingStatus : null;
      
      // For proposed bookings (optioned/hold status), fetch all records without date restrictions
      // For other statuses, use extended date range
      let fromDate, toDate;
      if (statusFilter && (statusFilter.includes('optioned') || statusFilter.includes('hold'))) {
        // No date restrictions for proposed bookings - fetch all
        fromDate = null;
        toDate = null;
      } else {
        // Extend the date range to ensure we get enough events for other bookings
        fromDate = moment(startDate).subtract(120, 'days').format('YYYY-MM-DD');
        toDate = moment(endDate).add(120, 'days').format('YYYY-MM-DD');
      }
      
      // Prepare excludeStatuses parameter - always exclude canceled, deleted and talentOnHold
      const excludeStatuses = 'canceled,deleted,talentOnHold';
      
      // Check if we're making the same request as before
      const currentParams = {
        fromDate,
        toDate,
        statusFilter,
        filters: JSON.stringify(filters)
      };
      
      // If nothing has changed and we're not forcing a refresh, don't make the API call again
      if (!forceRefresh && 
          lastFetchParams.current.fromDate === fromDate &&
          lastFetchParams.current.toDate === toDate &&
          lastFetchParams.current.statusFilter === statusFilter &&
          lastFetchParams.current.filters === currentParams.filters) {
        // Just end loading and return, as the events are already loaded
        setLoading(false);
        return;
      }
      
      // Update the last fetch params
      lastFetchParams.current = currentParams;
      
      try {
        // Use getAllBookings with status filtering
        console.log('CalendarContext: Making API call with params:', {
          page: 1,
          limit: 10000,
          fromDate,
          toDate,
          statusFilter,
          excludeStatuses
        });
        
        const response = await bookingService.getAllBookings(
          1, 
          10000, 
          fromDate, 
          toDate, 
          statusFilter, 
          excludeStatuses
        );
        
        console.log('CalendarContext: API response:', response);
        
        if (!response || !response.data || !response.data.bookings) {
          setError('Failed to load events: Invalid response');
          setEvents([]);
          setLoading(false);
          return;
        }
        
        const bookingsData = response.data.bookings;
        
        if (bookingsData && bookingsData.length > 0) {
          // Create formatted events from the bookings data
          const formattedEvents = bookingsData.map(booking => {
            // Make sure we have valid dates - always use start_date as the primary date
            let startDate, endDate;
            
            // Handle date fields which could be nested objects with timestamps or direct string dates
            if (booking.start_date && typeof booking.start_date === 'object' && booking.start_date._seconds) {
              // Firebase Timestamp format
              startDate = new Date(booking.start_date._seconds * 1000);
            } else if (booking.start_date) {
              // Direct date string
              startDate = new Date(booking.start_date);
            } else {
              startDate = new Date();
            }
            
            if (booking.end_date && typeof booking.end_date === 'object' && booking.end_date._seconds) {
              // Firebase Timestamp format
              endDate = new Date(booking.end_date._seconds * 1000);
            } else if (booking.end_date) {
              // Direct date string
              endDate = new Date(booking.end_date);
            } else {
              endDate = startDate;
            }
            
            // Extract client and talent - handle both nested objects or direct strings
            const client = typeof booking.client === 'object' ? booking.client?.label || 'Unknown' 
              : booking.client_name || booking.client || 'Unknown';
            
            const talent = typeof booking.talent === 'object' ? booking.talent?.label || 'No talent' 
              : booking.talent_name || booking.talent || 'No talent';
            
            // Format title based on booking type
            let formattedTitle;
            if (booking.set === "digital Production") {
              // KIND orders identified by set = "digital Production"
              // Use the actual title from the booking if it exists (includes day count)
              formattedTitle = booking.title || `KIND: ${client}`;
            } else if (talent && talent !== 'No talent') {
              formattedTitle = `${client} - ${talent}`;
            } else {
              formattedTitle = client;
            }
            
            const position = typeof booking.position === 'object' ? booking.position?.label || '' 
              : booking.position || '';
            
            // Create the formatted event with ALL original booking fields preserved
            return {
              // Keep ALL original fields from the booking
              ...booking,
              
              // Add our formatted fields
              id: booking.id,
              title: booking.title || `${formattedTitle} (${booking.status || 'Unknown'})`,
              date: startDate,
              start: startDate,
              start_date: format(startDate, 'yyyy-MM-dd'),
              end: endDate,
              end_date: format(endDate, 'yyyy-MM-dd'),
              allDay: true,
              client: client,
              talent: talent,
              position: position,
              
              // Ensure these specific fields are available and correctly named
              created_at: booking.created_at,
              created_by: booking.created_by,
              proposedBy: booking.proposed_by || booking.proposedBy,
              processing_by: booking.processing_by,
              is_being_processed: booking.is_being_processed === 1 || booking.is_being_processed === true,
              studio: booking.studio,
              location_name: booking.location_name,
              location_city: booking.location_city,
              location_short: booking.location_short,
              sonstige_info: booking.sonstige_info || booking.SonstigeInfo || booking.additional_info,
              
              // UI state
              showDetail: false,
              group_color: booking.group_color || booking.groupColor,
              color: getStatusColor(booking.status),
              durationInMinutes: calculateDuration(booking.start_time || booking.startTime, booking.end_time || booking.endTime) || 60
            };
          });
          
          // Apply remaining client-side filtering
          let filteredEvents = formattedEvents;
          
          // Apply booking status filter (only if we don't have initial events)
          if (filters.bookingStatus && !hasInitialEvents.current) {
            const statusArray = filters.bookingStatus.split(',').map(s => s.trim());
            console.log('Calendar filtering - Status filter:', statusArray);
            console.log('Calendar filtering - Available event statuses:', [...new Set(formattedEvents.map(e => e.status))]);
            
            filteredEvents = filteredEvents.filter(event => {
              const isIncluded = statusArray.includes(event.status);
              if (!isIncluded) {
                console.log(`Calendar filtering - Excluding event ${event.id} with status "${event.status}" (not in ${JSON.stringify(statusArray)})`);
              }
              return isIncluded;
            });
            
            console.log('Calendar filtering - Events after status filter:', filteredEvents.length);
          }
          
          if (filters.userEmail) {
            filteredEvents = filteredEvents.filter(event => 
              event.proposedBy === filters.userEmail
            );
          }
          
          if (filters.client) {
            filteredEvents = filteredEvents.filter(event => 
              event.client === filters.client
            );
          }
          
          if (filters.set) {
            filteredEvents = filteredEvents.filter(event => 
              event.set === filters.set
            );
          }
          
          if (filters.location) {
            filteredEvents = filteredEvents.filter(event => {
              // Extract location string from the event's location field
              let eventLocationString = '';
              if (event.location && typeof event.location === 'object') {
                eventLocationString = event.location.short || event.location.name || event.location.city || '';
              } else if (typeof event.location === 'string') {
                eventLocationString = event.location;
              }
              
              return eventLocationString === filters.location;
            });
          }
          
          // Apply talent group filter
          if (filters.talentGroup) {
            const selectedGroup = talentGroups.find(g => g.id === filters.talentGroup);
            if (selectedGroup && selectedGroup.talentIds && selectedGroup.talentIds.length > 0) {
              filteredEvents = filteredEvents.filter(event => {
                // Check if event's talent_id or sql_talent_id is in the group's talentIds
                const eventTalentId = event.talent_id || event.sql_talent_id;
                if (!eventTalentId) return false;
                // Convert to string for comparison since talentIds might be strings or numbers
                return selectedGroup.talentIds.some(id => 
                  String(id) === String(eventTalentId) || 
                  Number(id) === Number(eventTalentId)
                );
              });
            }
          }
          
          // Apply search filter if searchTerm exists
          if (filters.searchTerm && filters.searchTerm.trim() !== '') {
            const searchTerm = filters.searchTerm.toLowerCase().trim();
            filteredEvents = filteredEvents.filter(event => {
              // Search across multiple fields
              const searchableFields = [
                event.talent,           // Talent name
                event.client,          // Client name
                event.proposedBy,      // Proposed by (user)
                event.position,        // Position/function
                event.boktitle,        // Booking title
                event.SonstigeInfo,    // Additional info
                event.status,          // Status
                event.set,             // Set type
                // Location (handle object or string)
                typeof event.location === 'object' 
                  ? (event.location?.short || event.location?.name || event.location?.city || '')
                  : (event.location || ''),
                // KPI if available
                event.kpi,
                // Option if available  
                event.option
              ];
              
              // Check if any field contains the search term
              return searchableFields.some(field => {
                if (field && typeof field === 'string') {
                  return field.toLowerCase().includes(searchTerm);
                }
                return false;
              });
            });
          }
          
          // Set the events state
          setEvents(filteredEvents);
          
          // Generate filter options based on the events
          populateFilterOptions(filteredEvents);
        } else {
          setEvents([]);
        }
      } catch (apiError) {
        setError(`Failed to load events: ${apiError.message}`);
        setEvents([]);
      }
    } catch (error) {
      setError(`Failed to load events: ${error.message}`);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Helper to calculate duration from startTime and endTime
  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 60; // Default to 1 hour
    
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;
    
    return endMinutes - startMinutes > 0 ? endMinutes - startMinutes : 60;
  };
  
  // Helper to get color based on booking status
  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return '#15a326';
      case 'new order':
        return '#ffffff';
      case 'requested':
        return '#FF8806';
      case 'talentaccepted':
        return '#3de651';
      case 'declined request':
        return '#ff3c00';
      case 'optioned':
        return '#0060ff';
      case 'optionSolved':
        return '#FFFF00';
      case 'hold':
        return '#800080';
      case 'lano':
        return '#116c11';
      case 'Booked':
        return '#ADD8E6';
      default:
        return '#606060';
    }
  };

  // Generate dates for the current month view
  const getDaysForMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });
    
    const days = [];
    let day = startDate;
    
    while (day <= endDate) {
      days.push({
        date: day,
        isCurrentMonth: isSameMonth(day, monthStart),
        isToday: isSameDay(day, new Date()),
        isSelected: isSameDay(day, selectedDate),
      });
      day = addDays(day, 1);
    }
    
    return days;
  };

  // Generate dates for the current week view - 7 days (Monday to Sunday)
  const getDaysForWeekView = () => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    
    const days = [];
    let day = weekStart;
    
    // Loop for 7 days (Monday to Sunday)
    for (let i = 0; i < 7; i++) {
      days.push({
        date: day,
        isToday: isSameDay(day, new Date()),
        isSelected: isSameDay(day, selectedDate),
      });
      day = addDays(day, 1);
    }
    
    return days;
  };

  // Get hours for day view
  const getHoursForDayView = () => {
    const hours = [];
    for (let i = 0; i < 24; i++) {
      hours.push({
        hour: i,
        label: format(new Date().setHours(i, 0, 0, 0), 'h:mm a'),
      });
    }
    return hours;
  };

  // Navigation functions
  const goToPreviousMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const goToPreviousWeek = () => {
    setCurrentDate(addDays(currentDate, -7));
  };

  const goToNextWeek = () => {
    setCurrentDate(addDays(currentDate, 7));
  };

  const goToPreviousDay = () => {
    setCurrentDate(addDays(currentDate, -1));
  };

  const goToNextDay = () => {
    setCurrentDate(addDays(currentDate, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  // Select a date
  const onDateSelect = (date) => {
    setSelectedDate(date);
  };

  // Get events for a specific date
  const getEventsForDate = (date) => {
    if (!events) return [];
    
    return events.filter(event => {
      // Parse event.start_date or event.start as fallback
      const eventDate = event.start_date ? 
        (typeof event.start_date === 'string' ? parseISO(event.start_date) : new Date(event.start_date)) :
        (event.start ? (typeof event.start === 'string' ? parseISO(event.start) : new Date(event.start)) : null);
      
      // Skip events without a date
      if (!eventDate) return false;
      
      return isSameDay(date, eventDate);
    });
  };

  // Optimized version of setEventFilters to prevent rerendering loops
  const setEventFilters = (newFilters) => {
    // Prevent excessive re-renders by checking if the filters actually changed
    const hasChanged = Object.entries(newFilters).some(([key, value]) => {
      // If filter key not in current filters or value is different
      return filters[key] !== value;
    });
    
    if (hasChanged) {
      // Use a function updater to ensure we're working with the latest state
      setFilters(prevFilters => {
        // Create a new object with all the previous filters
        const updatedFilters = { ...prevFilters };
        
        // Apply each new filter value
        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === null || value === undefined) {
            // If value is null/undefined, remove this filter
            delete updatedFilters[key];
          } else {
            // Otherwise update/add the filter
            updatedFilters[key] = value;
          }
        });
        
        return updatedFilters;
      });
    }
  };

  // Add this helper function to format events from the API
  const formatEventFromApi = (apiEvent) => {
    // Format title based on booking type
    let eventTitle;
    if (apiEvent.set === "digital Production") {
      // KIND orders identified by set = "digital Production"
      // Use the actual title from the booking if it exists (includes day count)
      eventTitle = apiEvent.title || `KIND: ${apiEvent.client || 'Unknown'}`;
    } else if (apiEvent.talent && apiEvent.talent !== 'No talent') {
      eventTitle = `${apiEvent.client || 'Unknown'} - ${apiEvent.talent}`;
    } else {
      eventTitle = apiEvent.client || 'Unknown';
    }
    
    return {
      id: apiEvent.id,
      title: eventTitle,
      start: apiEvent.startDate ? new Date(apiEvent.startDate) : new Date(),
      end: apiEvent.endDate ? new Date(apiEvent.endDate) : new Date(),
      allDay: true,
      client: apiEvent.client || 'Unknown',
      talent: apiEvent.talent || 'No talent',
      company: apiEvent.company || '',
      status: apiEvent.status || '',
      set: apiEvent.set || '',
      kpi: apiEvent.kpi || '',
      option: apiEvent.option || '',
      proposedBy: apiEvent.proposedBy || '',
      position: apiEvent.position || '',
      startTime: apiEvent.startTime || '',
      endTime: apiEvent.endTime || '',
      location: apiEvent.location || '',
      SonstigeInfo: apiEvent.additionalInfo || '',
      group: apiEvent.group || '',
      groupColor: apiEvent.groupColor || '',
      boktitle: apiEvent.title || '',
      color: getStatusColor(apiEvent.status)
    };
  };

  // Format booking events from API to calendar events
  const formatBookingEvents = (bookings) => {
    if (!bookings || !Array.isArray(bookings)) {
      console.error('Invalid bookings data:', bookings);
      return [];
    }
    
    return bookings.map(booking => {
      // Start and end dates
      const startDate = booking.startDate ? new Date(booking.startDate) : new Date();
      const endDate = booking.endDate ? new Date(booking.endDate) : new Date(startDate);
      
      // Set end date to be same as start date if it's earlier (invalid)
      if (endDate < startDate) {
        endDate.setTime(startDate.getTime());
      }
      
      // Add an extra day for display as all-day events
      if (endDate && endDate instanceof Date) {
        endDate.setDate(endDate.getDate() + 1);
      }
      
      // Format title based on booking type
      let bookingTitle;
      if (booking.set === "digital Production") {
        // KIND orders identified by set = "digital Production"
        // Use the actual title from the booking if it exists (includes day count)
        bookingTitle = booking.title || `KIND: ${booking.client || 'Unknown'}`;
      } else if (booking.talent && booking.talent !== 'No talent') {
        bookingTitle = `${booking.client || 'Unknown'} - ${booking.talent}`;
      } else {
        bookingTitle = booking.client || 'Unknown';
      }
      
      return {
        id: booking.id,
        title: bookingTitle,
        start: startDate,
        end: endDate,
        allDay: true,
        client: booking.client || 'Unknown',
        talent: booking.talent || 'No talent',
        status: booking.status || 'new order',
        set: booking.set || '',
        location: booking.location || '',
        proposedBy: booking.proposedBy || '',
        position: booking.position || '',
        boktitle: booking.title || '',
        startTime: booking.startTime || '',
        endTime: booking.endTime || '',
        group: booking.group || '',
        groupColor: booking.groupColor || '',
        kpi: booking.kpi || '',
        option: booking.option || '',
        SonstigeInfo: booking.additionalInfo || ''
      };
    });
  };

  // Fetch talent groups on mount
  useEffect(() => {
    const fetchTalentGroups = async () => {
      try {
        const response = await talentGroupService.getAllGroups(1, 100);
        if (response.data?.success && response.data?.data?.groups) {
          const groups = response.data.data.groups.map(group => ({
            id: group.id.toString(),
            name: group.name,
            talentIds: group.talent_ids || [],
            createdAt: group.created_at
          }));
          setTalentGroups(groups);
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          const groups = response.data.data.map(group => ({
            id: group.id.toString(),
            name: group.name,
            talentIds: group.talent_ids || [],
            createdAt: group.created_at
          }));
          setTalentGroups(groups);
        }
      } catch (error) {
        console.error('Error loading talent groups:', error);
      }
    };

    fetchTalentGroups();
  }, []);

  const value = {
    currentDate,
    selectedDate,
    view,
    events,
    loading,
    error,
    userOptions,
    clientOptions,
    setOptions,
    locationOptions,
    statusOptions,
    talentGroups,
    filters,
    setCurrentDate,
    setSelectedDate,
    setView,
    setEvents,
    setEventFilters,
    updateFilters: setEventFilters, // Alias for compatibility
    getEventsForDate,
    getDaysForMonthView,
    getDaysForWeekView,
    getHoursForDayView,
    goToPreviousMonth,
    goToNextMonth,
    goToPreviousWeek,
    goToNextWeek,
    goToPreviousDay,
    goToNextDay,
    goToToday,
    onDateSelect,
    calculateDuration,
    getStatusColor,
    fetchBookingEvents
  };

  return (
    <CalendarContext.Provider value={value}>
      {children}
    </CalendarContext.Provider>
  );
}

export default CalendarContext; 